import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useToast,
  VStack,
  Image,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  IconButton,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useAuth } from '../hooks/useAuth';
import { validatePassword } from '../utils/passwordUtils';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [passwordValidation, setPasswordValidation] = useState(validatePassword(''));
  
  const { token } = useParams();
  const { resetPassword, verifyResetToken } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    const checkToken = async () => {
      const isValid = await verifyResetToken(token);
      if (!isValid) {
        toast({
          title: 'Invalid or Expired Link',
          description: 'This password reset link is invalid or has expired. Please request a new one.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/forgot-password');
      }
    };
    checkToken();
  }, [token, navigate, toast, verifyResetToken]);

  useEffect(() => {
    setPasswordValidation(validatePassword(password));
  }, [password]);

  const validateForm = () => {
    const errors = {};
    const validation = validatePassword(password);

    if (!validation.isValid) {
      errors.password = 'Password does not meet strength requirements';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await resetPassword(token, password);
      
      toast({
        title: 'Password Reset Successful',
        description: 'Your password has been successfully reset. Please login with your new password.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      navigate('/login');
    } catch (error) {
      let errorMessage = 'Failed to reset password';
      
      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid or expired reset token';
            break;
          case 429:
            errorMessage = 'Too many attempts. Please try again later';
            break;
          default:
            errorMessage = error.response.data?.message || 'An error occurred';
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection';
      }

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      minH="calc(100vh - 64px)"
      bg={useColorModeValue('gray.50', 'gray.900')}
      py={12}
    >
      <Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', sm: '8' }}>
        <Stack spacing="8">
          <Stack spacing="6" align="center">
            <Image
              src="/logo.png"
              alt="Budget Brilliance"
              h="60px"
            />
            <Stack spacing="3" textAlign="center">
              <Heading size="lg" color="brand.500">
                Reset Your Password
              </Heading>
              <Text color="gray.500">
                Enter your new password below
              </Text>
            </Stack>
          </Stack>

          <Box
            py={{ base: '0', sm: '8' }}
            px={{ base: '4', sm: '10' }}
            bg={bgColor}
            boxShadow="lg"
            borderRadius={{ base: 'none', sm: 'xl' }}
            borderWidth="1px"
            borderColor={borderColor}
          >
            <form onSubmit={handleSubmit}>
              <Stack spacing="6">
                <FormControl isInvalid={formErrors.password}>
                  <FormLabel>New Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      disabled={isLoading}
                    />
                    <InputRightElement>
                      <IconButton
                        variant="ghost"
                        icon={showPassword ? <ViewIcon /> : <ViewOffIcon />}
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{formErrors.password}</FormErrorMessage>
                  {password && <PasswordStrengthIndicator validationResult={passwordValidation} />}
                </FormControl>

                <FormControl isInvalid={formErrors.confirmPassword}>
                  <FormLabel>Confirm Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      disabled={isLoading}
                    />
                    <InputRightElement>
                      <IconButton
                        variant="ghost"
                        icon={showConfirmPassword ? <ViewIcon /> : <ViewOffIcon />}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{formErrors.confirmPassword}</FormErrorMessage>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="brand"
                  size="lg"
                  fontSize="md"
                  isLoading={isLoading}
                  loadingText="Resetting..."
                  isDisabled={!passwordValidation.isValid || password !== confirmPassword}
                >
                  Reset Password
                </Button>
              </Stack>
            </form>
          </Box>

          <Text textAlign="center">
            <ChakraLink
              as={Link}
              to="/login"
              color="brand.500"
              fontWeight="semibold"
              _hover={{ textDecoration: 'underline' }}
            >
              Back to Login
            </ChakraLink>
          </Text>
        </Stack>
      </Container>
    </Box>
  );
};

export default ResetPassword;
